import React from 'react';

import { Flex } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { AcceptOrDenyNomination } from 'screens/performanceReview/LandingPage/components/ReviewsPendingOnMeComps/ReviewRowDisplay';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { AddPerson, BulletList, Continue, Play } from 'assets/assetsV1/iconComponents';

import { downloadOrOpenFile, getReviewFormRoute } from 'constants/helper';
import { sendOpenedNominationPanelEvent, sendRespondedToFeedbackRequest } from 'constants/mixpanelHelper';

import { acceptFeedback, declineFeedback } from 'services/continuousFeedback';
import { showNominatePeers } from 'services/nominatePeers';
import { acceptNomination, rejectNomination } from 'services/performanceReview';
import { showErrorSnackbar } from 'services/snackbar';

import { Button } from 'uiToolkitV1/Button';

const REVIEW_SUBMISSION_STATUS_MAP = {
  NOT_STARTED: {
    actionText: 'Start',
    statusText: 'Not Started',
    Icon: Play,
    infoChipColor: 'black',
  },
  IN_PROGRESS: {
    actionText: 'Continue',
    statusText: 'In progress',
    Icon: Continue,
    infoChipColor: 'yellow',
  },
  SUBMITTED: {
    actionText: 'View result',
    statusText: 'Completed',
    Icon: BulletList,
    infoChipColor: 'green',
  },
  ACKNOWLEDGED: {
    actionText: 'View result',
    statusText: 'Acknowledged',
    Icon: BulletList,
    infoChipColor: 'green',
  },
};

const isNominated = (review) => review.providerType === 'PEER' && review.peerStatus === 'NOMINATED';

const ReviewActionButton = ({ review, nominationsCount }) => {
  const dispatch = useDispatch();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const goToFormReviewRoute = (reviewId, employeeId) => {
    const reviewFormUrl = `${window.location.origin}${getReviewFormRoute(reviewId, employeeId)}`;
    window.open(reviewFormUrl);
  };

  const handleViewDetailsClick = () => {
    goToFormReviewRoute(review.feedbackForm.id, review.receiverEmployeeId);
  };
  const handleSubmissionStatusClick = () => {
    if (review.summaryFilePath) {
      downloadOrOpenFile(review.summaryFilePath);
    } else {
      handleViewDetailsClick();
    }
  };

  const handleNominateClick = () => {
    sendOpenedNominationPanelEvent({
      eventProperties: {
        reviewCycleId: review.id,
        source: 'Pending Reviews',
        empId: loggedInEmployee.id,
      },
    });
    dispatch(showNominatePeers({ reviewId: review.feedbackForm.id }));
  };

  const handleAcceptClick = () => {
    const acceptTrigger = review.providerType === 'PEER' ? acceptNomination : acceptFeedback;
    const requestDetails = () => {
      if (review.providerType === 'PEER') {
        return {
          reviewId: review.feedbackForm.id,
          employeeId: review.receiverEmployeeId,
        };
      }
      return {
        feedbackId: review.id,
      };
    };

    acceptTrigger(requestDetails())
      .then(() => {
        if (review.providerType !== 'PEER') {
          sendRespondedToFeedbackRequest({
            eventProperties: {
              response: 'Accept',
            },
          });
        }
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Some error occurred'));
      });
  };

  const handleRejectClick = () => {
    const rejectTrigger = review.providerType === 'PEER' ? rejectNomination : declineFeedback;
    const requestDetails = () => {
      if (review.providerType === 'PEER') {
        return {
          reviewId: review.feedbackForm.id,
          employeeId: review.receiverEmployeeId,
        };
      }
      return {
        feedbackId: review.id,
      };
    };

    rejectTrigger(requestDetails())
      .then(() => {
        if (review.providerType !== 'PEER') {
          sendRespondedToFeedbackRequest({
            eventProperties: {
              response: 'Reject',
            },
          });
        }
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Some error occurred'));
      });
  };

  if (review.providerType === 'NOMINATOR') {
    return (
      <Flex className="action" justify="flex-end">
        <Button variant="rowButtonOutlined" onClick={handleNominateClick} StartIcon={AddPerson}>
          {nominationsCount === 0 ? 'Nominate' : 'Edit nominations'}
        </Button>
      </Flex>
    );
  }

  if (isNominated(review)) {
    return (
      <Flex className="action" justify="flex-end">
        <AcceptOrDenyNomination handleAcceptClick={handleAcceptClick} handleRejectClick={handleRejectClick} />
      </Flex>
    );
  }

  return (
    <Flex className="action" justify="flex-end">
      <Button
        variant="rowButtonOutlined"
        onClick={handleSubmissionStatusClick}
        StartIcon={REVIEW_SUBMISSION_STATUS_MAP?.[review.submissionStatus]?.Icon ?? null}
      >
        {REVIEW_SUBMISSION_STATUS_MAP[review.submissionStatus].actionText}
      </Button>
    </Flex>
  );
};

export { ReviewActionButton };
